import {
  Anchor,
  Box,
  Button,
  Center,
  Group,
  Modal,
  Stack,
  Text,
  Title
} from "@mantine/core";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCheck,
  IconMinus,
  IconPencil,
  IconPlus
} from "@tabler/icons-react";
import { useState } from "react";
import { EditUsersAssignedToRole } from "./settings/community/-EditUsersAssignedToRole";
import { type CommunityInList } from "@/models";
import { getAllCommunitiesQuery, useApi } from "@/api";
import { updateCommunitySparkProgram } from "@/api/communities";
import { DataTable } from "@/components/DataTable";
import { showSuccess } from "@/notifications/Notifications";
import {
  canSetCommunityAdmin,
  canSetCommunityIsSpark,
  canViewCommunitySettings
} from "@/security/logic";

export const Route = createFileRoute("/settings/")({
  component: Settings,
  preload: false,
  loader: async ({ context, location }) => {
    const { userClaim } = context;
    if (!canViewCommunitySettings(userClaim, "somewhere")) {
      throw redirect({
        to: "/notAuthorized/$",
        params: { _splat: location.href }
      });
    }
    const { secureAxios } = context.apiHook;
    const communities = await context.queryClient.fetchQuery(
      getAllCommunitiesQuery(secureAxios)
    );

    return {
      communities
    };
  }
});

function Settings() {
  const { userClaim } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();
  const { data: communities } = useQuery(getAllCommunitiesQuery(secureAxios));
  const [confirmAddModalOpened, setConfirmAddModalOpened] = useState(false);
  const [selectedCommunity, setSelectedCommunity] =
    useState<CommunityInList | null>(null);
  const [isRemovingFromSpark, setIsRemovingFromSpark] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [communityAdminModalOpened, setCommunityAdminModalOpened] =
    useState(false);
  const [communityBeingEdited, setCommunityBeingEdited] =
    useState<CommunityInList | null>(null);
  const showActionColumn = canSetCommunityIsSpark(userClaim, "somewhere");

  const handleUpdateSparkProgram = async () => {
    if (!selectedCommunity) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateCommunitySparkProgram(
        secureAxios,
        selectedCommunity.id,
        !isRemovingFromSpark
      );
      await queryClient.invalidateQueries({
        queryKey: getAllCommunitiesQuery(secureAxios).queryKey
      });
      showSuccess(
        isRemovingFromSpark
          ? `${selectedCommunity.name} has been removed from the Spark Program`
          : `${selectedCommunity.name} has been added to the Spark Program`
      );
      setConfirmAddModalOpened(false);
    } catch (error: unknown) {
      console.error("Failed to update community:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columnHelper = createColumnHelper<CommunityInList>();

  return (
    <div>
      <Title order={3}>Administration Settings</Title>
      <Box maw={600}>
        <DataTable
          data={communities ?? []}
          columns={[
            columnHelper.accessor((row) => row.name, {
              id: "name",
              header: "Community Name",
              cell: (info) => {
                const community = info.row.original;
                const fullName = info.getValue();
                if (community.isSparkProgram) {
                  return (
                    <Anchor
                      component={Link}
                      to={`/settings/community/${community.id}`}
                    >
                      {fullName}
                    </Anchor>
                  );
                }
                return <Text>{fullName}</Text>;
              }
            }),
            columnHelper.accessor((row) => row.communityAdministrators, {
              id: "communityAdministrators",
              header: "Community Administrator",
              cell: (info) => {
                const community = info.row.original;
                const admins = info.getValue();

                // Show "Edit Administrator" button if:
                // - Row is a Spark Program, but
                // - there are no community admins, and
                // - the current user can set the community admin
                if (
                  community.isSparkProgram &&
                  admins.length === 0 &&
                  canSetCommunityAdmin(userClaim, community.id)
                ) {
                  return (
                    <Button
                      leftSection={<IconPencil size={17} />}
                      onClick={() => {
                        setCommunityBeingEdited(community);
                        setCommunityAdminModalOpened(true);
                      }}
                    >
                      Edit Administrator
                    </Button>
                  );
                }

                // Otherwise, list the community administrators
                return admins.map((admin) => <div key={admin}>{admin}</div>);
              }
            }),
            columnHelper.accessor((row) => row.isSparkProgram, {
              id: "isSparkProgram",
              header: "Spark Program",
              meta: { style: { textAlign: "center", width: "1%" } },
              cell: (info) => {
                if (info.getValue()) {
                  return (
                    <Center>
                      <IconCheck size={17} />
                    </Center>
                  );
                }
                return null;
              }
            }),
            ...(showActionColumn
              ? [
                  columnHelper.display({
                    id: "actions",
                    header: "Action",
                    meta: { style: { textAlign: "center", width: "1%" } },
                    cell: (info) => {
                      const community = info.row.original;
                      if (!canSetCommunityIsSpark(userClaim, community.id)) {
                        return null;
                      }
                      return (
                        <Button
                          variant="subtle"
                          size="compact-md"
                          title={
                            community.isSparkProgram
                              ? "Remove from Spark"
                              : "Add to Spark"
                          }
                          onClick={() => {
                            setSelectedCommunity(community);
                            setIsRemovingFromSpark(community.isSparkProgram);
                            setConfirmAddModalOpened(true);
                          }}
                        >
                          {community.isSparkProgram ? (
                            <IconMinus size={17} />
                          ) : (
                            <IconPlus size={17} />
                          )}
                        </Button>
                      );
                    }
                  })
                ]
              : [])
          ]}
        />
      </Box>

      <Modal
        opened={confirmAddModalOpened}
        onClose={() => !isSubmitting && setConfirmAddModalOpened(false)}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        title={isRemovingFromSpark ? "Remove from Spark" : "Add to Spark"}
        size="md"
      >
        <Stack gap="md">
          <Text>
            Are you sure you would like to
            {isRemovingFromSpark ? " remove " : " add "}
            <Text span fw={700}>
              {selectedCommunity?.name}
            </Text>{" "}
            {isRemovingFromSpark
              ? "from the Spark Program?"
              : "to the Spark Program?"}
          </Text>
          {isRemovingFromSpark && (
            <Text size="sm" c="dimmed">
              This will only stop the import of new prospects and information
              from Welcome Home for this community. Existing prospects and
              information will not be removed.
            </Text>
          )}
          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => setConfirmAddModalOpened(false)}
              disabled={isSubmitting}
            >
              No, Go Back
            </Button>
            <Button
              onClick={() => {
                void handleUpdateSparkProgram();
              }}
              color={isRemovingFromSpark ? "red" : undefined}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isRemovingFromSpark
                ? "Yes, Remove from Spark"
                : "Yes, Add to Spark"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <EditUsersAssignedToRole
        opened={communityAdminModalOpened}
        onClose={() => {
          setCommunityAdminModalOpened(false);
          setCommunityBeingEdited(null);
        }}
        onSuccess={() => {
          void queryClient.invalidateQueries({
            queryKey: getAllCommunitiesQuery(secureAxios).queryKey
          });
        }}
        roleFullDisplayName="Community Administrator"
        roleKey="CommunityAdmin"
        communityId={communityBeingEdited?.id ?? 0}
        communityName={communityBeingEdited?.name ?? null}
        initialAssignedUsers={[]}
        currentAssignedUsers={undefined}
      />
    </div>
  );
}
